// The Academy: scholarships and scholars (Basic), and the premium mass-scholarship tools (Advanced).
import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/api/client";
import { art } from "@/kit/art";
import { CostLine, Countdown, GoldRange, ScreenHero, Stepper, Toggle, UnitMedal } from "@/kit/city";
import { IconClose } from "@/kit/icons";
import { Confirm } from "@/kit/overlays";
import { Bar, Button, Card, Disc, Medallion, Row, RowText, Seg, cx } from "@/kit/primitives";
import { useNow } from "@/state/clock";
import { BUILDING_BLURB, RESOURCE_KEYS, RESOURCE_NAMES, type ResourceKey } from "@/game/constants";
import { fmtNum } from "@/game/format";
import { parseMinSec } from "@/game/time";
import { buildingMap, capacity, effectExpiry, recruitQueue, shortfall, storeItem } from "@/game/city";
import { CityPage, useCity } from "./CityRoute";

type Tab = "basic" | "advanced";

export function Academy() {
  const { v, wid, vid, actions, base } = useCity();
  const nav = useNavigate();
  const now = useNow();
  const [tab, setTab] = useState<Tab>("basic");
  const [qty, setQty] = useState(1);
  const [buffer, setBuffer] = useState<Record<ResourceKey, number>>({ r_w: 20000, r_c: 20000, r_i: 20000 });
  const [cancelScholar, setCancelScholar] = useState<number | null>(null);
  const scholarQueue = useMemo(() => (v ? recruitQueue(v, "b_a") : []), [v]);
  if (!v) return <CityPage>{null}</CityPage>;
  const a = v.academy;
  const level = buildingMap(v).b_a?.level ?? 0;
  const prestigeEnd = effectExpiry(v, "increase_influence");
  const prestigeOn = !!prestigeEnd && prestigeEnd > now;
  const premiumEnd = effectExpiry(v, "mass_scholarships");
  const premiumOn = !!premiumEnd && premiumEnd > now;
  const prestigeItem = storeItem(v, "prestige");
  const premiumItem = storeItem(v, "mass_scholarships");
  const isFarm = (v.schoolarship_farms as number[] | undefined)?.includes(vid) ?? false;
  const gold = v.gold?.amount_owned ?? 0;

  const shipCost = a?.scholarship_cost ?? { r_w: 0, r_c: 0, r_i: 0 };
  const maxShips = Math.max(0, Math.min(...RESOURCE_KEYS.map((r) => (shipCost[r] > 0 ? Math.floor((v.resources?.[r] ?? 0) / shipCost[r]) : 999))));
  const n = Math.min(Math.max(1, qty), Math.max(1, maxShips));
  const scholarCost = a?.scholar_cost ? { r_w: a.scholar_cost.r_w, r_c: a.scholar_cost.r_c, r_i: a.scholar_cost.r_i, p_u: a.scholar_cost.p_u, time: parseMinSec(a.scholar_cost.time) } : null;
  const canEducate = a?.num_scholars_can_still_educate ?? 0;
  const scholarShort = scholarCost ? shortfall(v, scholarCost) : null;
  const scholarOk = level > 0 && canEducate > 0 && scholarShort && !Object.keys(scholarShort.short).length && !scholarShort.pop;
  // Limit n needs n(n+1)/2 scholarships in total (back end ScholarUtils.ScholarshipsFromLimit).
  const nextLimit = a?.next_scholar_limit ?? 0;
  const limitNeedTotal = (nextLimit * (nextLimit + 1)) / 2;
  const limitHaveTotal = a ? Math.max(0, limitNeedTotal - a.num_missing_scholarships_for_next_scholar_limit) : 0;
  const limitProgress = limitNeedTotal > 0 ? Math.max(0, Math.min(1, limitHaveTotal / limitNeedTotal)) : 0;

  return (
    <CityPage>
      <div className="stack">
        <ScreenHero
          level={level}
          title="Academy"
          blurb={BUILDING_BLURB.b_a}
          art={art.hero.academy}
          artWidth="50%"
          onBack={() => nav(base)}
          onGear={() => nav(`${base}/hall`)}
          gearLabel="City overview"
          stats={[
            { label: "Scholars", value: a?.scholars_total ?? 0, small: `of ${a?.scholar_limit ?? 0}` },
            { label: "Scholarships", value: a?.scholarships ?? 0 },
            prestigeOn ? { label: "Prestige", value: "+5", tone: "green" as const, small: "loyalty" } : { label: "Prestige", value: "Off", tone: "crim" as const },
          ]}
        />
        <Seg value={tab} onChange={setTab} options={[{ value: "basic", label: "Basic" }, { value: "advanced", label: "Advanced" }]} />

        {tab === "basic" && (
          <>
            <Card className="acad-card">
              <Medallion src={art.academy.scroll} alt="" size={84} className="acad-med" />
              <div className="acad-body">
                <div className="acad-head"><h2>Scholarships</h2><span className="aside">{fmtNum(a?.scholarships)} owned</span></div>
                <CostLine cost={shipCost} size="sm" />
                <div className="acad-row">
                  <Stepper value={n} onChange={setQty} min={1} max={Math.max(1, maxShips)} label="Scholarships" />
                  <GoldRange value={n} min={1} max={Math.max(1, maxShips)} onChange={setQty} label="Scholarships" />
                  <span className="muted" style={{ font: "400 var(--fs-meta)/1 var(--text)", whiteSpace: "nowrap" }}>Max {fmtNum(maxShips)}</span>
                </div>
                <Button kind="crimson" wide disabled={level === 0 || maxShips === 0} busy={actions.isBusy("scholarship")}
                  onClick={() => actions.run("scholarship", () => api.academy.createScholarship({ wid, vid }, n), { success: `${n} scholarship${n === 1 ? "" : "s"} bought.` })}>
                  Purchase {n} scholarship{n === 1 ? "" : "s"}
                </Button>
                {maxShips === 0 && level > 0 && <div className="c-note">Not enough resources for one scholarship.</div>}
              </div>
            </Card>

            <Card className="acad-card">
              <Medallion src={art.academy.crown} alt="" size={84} className="acad-med" />
              <div className="acad-body">
                <div className="acad-head"><h2>Scholars</h2><span className="aside">{fmtNum(a?.scholars_total)} of {fmtNum(a?.scholar_limit)}</span></div>
                {scholarCost && <CostLine cost={scholarCost} pop={scholarCost.p_u} time={scholarCost.time} size="sm" short={scholarShort?.short} />}
                <div className="equation">
                  {[
                    ["Limit", a?.scholar_limit ?? 0], ["Scholars", a?.scholars_total ?? 0], ["Training", a?.scholars_in_education ?? 0], ["Cities", a?.num_conquered_villages ?? 0],
                  ].map(([l, val], i) => (
                    <div key={String(l)} className="term">
                      {i > 0 && <span className="op">−</span>}
                      <div className="c-tile"><div className="val">{fmtNum(Number(val))}</div><div className="lbl">{l}</div></div>
                    </div>
                  ))}
                  <div className="term"><span className="op">=</span><div className={cx("c-tile", canEducate <= 0 && "shortfall")}><div className={cx("val", canEducate <= 0 ? "crimtxt" : "greentxt")}>{fmtNum(canEducate)}</div><div className="lbl">Can educate</div></div></div>
                </div>
                {a && (
                  <div className="limitline">
                    <span>Limit {nextLimit} needs <b className="gold">{fmtNum(limitNeedTotal)}</b> scholarships total · <b className="creamt">{fmtNum(limitHaveTotal)} / {fmtNum(limitNeedTotal)}</b>{a.num_missing_scholarships_for_next_scholar_limit > 0 && <> · {fmtNum(a.num_missing_scholarships_for_next_scholar_limit)} to go</>}</span>
                    <Bar value={limitProgress} />
                  </div>
                )}
                <Button kind="steel" wide disabled={!scholarOk} busy={actions.isBusy("scholar")}
                  onClick={() => actions.run("scholar", () => api.academy.recruitScholar({ wid, vid }), { success: "A scholar is being educated." })}>
                  Educate scholar
                </Button>
                {level > 0 && !scholarOk && <div className="c-note">{canEducate <= 0 ? "Save more scholarships to raise the limit." : scholarShort?.pop ? `Needs ${fmtNum(scholarCost?.p_u ?? 0)} free population.` : "Not enough resources."}</div>}
                {scholarQueue.length > 0 && (
                  <div className="rows" style={{ marginTop: "calc(var(--u) * 6)" }}>
                    {scholarQueue.map((e, i) => (
                      <Row key={i} className="goldedge">
                        <UnitMedal troop="t_n" tier={0} size={48} />
                        <RowText line="Scholar in education" meta={i === 0 ? "Studying now" : "Queued"} />
                        <div className="right"><div className="val" style={{ font: "600 var(--fs-value)/1 var(--text)" }}><Countdown endMs={e.endMs} /></div><Disc size="sm" label="Cancel scholar" onClick={() => setCancelScholar(e.item.id)}><IconClose /></Disc></div>
                      </Row>
                    ))}
                  </div>
                )}
              </div>
            </Card>

            {prestigeItem && (
              <Card className="acad-card">
                <Medallion src={art.academy.prestige} alt="" size={84} className="acad-med" glow={prestigeOn ? "ember" : undefined} />
                <div className="acad-body">
                  <div className="acad-head"><h2>Prestige</h2>{prestigeOn ? <span className="c-active">Active <small>· <Countdown endMs={prestigeEnd!} /></small></span> : <span className="aside">Off</span>}</div>
                  <p>Each scholar removes 5 more loyalty per attack. Every extension adds 24 hours.</p>
                  <Button kind="gold" wide disabled={gold < prestigeItem.cost} busy={actions.isBusy("prestige")}
                    onClick={() => actions.run("prestige", () => api.store.buy({ wid, vid }, "prestige", 1), { success: "Prestige extended by 24 hours." })}>
                    {prestigeOn ? "Extend 24 hours" : "Activate 24 hours"} <span className="sep" /> <span className="coin" /> {prestigeItem.cost}
                  </Button>
                </div>
              </Card>
            )}
          </>
        )}

        {tab === "advanced" && (
          <>
            <Card className="acad-card">
              <Medallion src={art.academy.decree} alt="" size={84} className="acad-med" />
              <div className="acad-body">
                <div className="acad-head"><h2>Mass scholarship</h2><span className="pilltag green">Free</span></div>
                <p>Every city with an Academy spends all its resources on scholarships.</p>
                <Button kind="crimson" wide busy={actions.isBusy("mass")} onClick={() => actions.run("mass", () => api.academy.createMassScholarships({ wid, vid }, "max_all"), { success: "Scholarships bought in every city." })}>Buy max</Button>
              </div>
            </Card>
            {premiumItem && (
              <Card className="acad-card">
                <Medallion src={art.academy.premium} alt="" size={84} className="acad-med" glow={premiumOn ? "ember" : undefined} />
                <div className="acad-body">
                  <div className="acad-head"><h2>Premium scholarship</h2>{premiumOn ? <span className="c-active">Active <small>· <Countdown endMs={premiumEnd!} compact /></small></span> : <span className="aside">{Math.round(premiumItem.duration / 86400000)} days</span>}</div>
                  <p>Buy scholarships for all your cities from any Academy.</p>
                  <Button kind="gold" wide disabled={gold < premiumItem.cost} busy={actions.isBusy("premium")}
                    onClick={() => actions.run("premium", () => api.store.buy({ wid, vid }, "mass_scholarships", 1), { success: "Premium Scholarship activated." })}>
                    {premiumOn ? "Extend" : "Activate"} <span className="sep" /> <span className="coin" /> {premiumItem.cost}
                  </Button>
                </div>
              </Card>
            )}
            <Card className="acad-card">
              <Medallion src={art.academy.farm} alt="" size={84} className="acad-med" />
              <div className="acad-body">
                <div className="acad-head"><h2>Scholarship farm</h2><span className="pilltag">Premium</span><span className="aside">{fmtNum((v.schoolarship_farms as number[] | undefined)?.length ?? 0)} farm cities</span></div>
                <p>Only farm cities spend their resources when you buy from the farm.</p>
                <Row>
                  <RowText line={<>Designate <b className="gold">{v.village?.city_name}</b> as a farm</>} />
                  <div className="right">
                    <Toggle on={isFarm} label="Scholarship farm" disabled={!premiumOn} busy={actions.isBusy("farm")}
                      onChange={(on) => actions.run("farm", () => (on ? api.academy.farmAdd({ wid, vid }) : api.academy.farmRemove({ wid, vid })))} />
                  </div>
                </Row>
                <Button kind="steel" wide disabled={!premiumOn} busy={actions.isBusy("farmbuy")} onClick={() => actions.run("farmbuy", () => api.academy.createMassScholarships({ wid, vid }, "max_selected"), { success: "Scholarships bought from the farm cities." })}>Buy from farm</Button>
                {!premiumOn && <div className="c-note">Needs Premium Scholarship.</div>}
              </div>
            </Card>
            <Card className="acad-card">
              <Medallion src={art.academy.buffer} alt="" size={84} className="acad-med" />
              <div className="acad-body">
                <div className="acad-head"><h2>Resource buffer</h2><span className="pilltag">Premium</span></div>
                <p>Every city keeps this much of each resource and spends the rest.</p>
                <div className="stack" style={{ gap: "calc(var(--u) * 10)" }}>
                  {RESOURCE_KEYS.map((r) => (
                    <div key={r} className="bufrow">
                      <img src={art.hud[r === "r_w" ? "wood" : r === "r_c" ? "clay" : "iron"]} alt={RESOURCE_NAMES[r]} />
                      <GoldRange value={buffer[r]} max={Math.max(1000, capacity(v))} step={1000} onChange={(n2) => setBuffer((b) => ({ ...b, [r]: n2 }))} label={`${RESOURCE_NAMES[r]} buffer`} disabled={!premiumOn} />
                      <span className="c-num" style={{ width: "calc(var(--u) * 150)", textAlign: "center", padding: "calc(var(--u) * 8)" }}>{fmtNum(buffer[r])}</span>
                    </div>
                  ))}
                </div>
                <Button kind="steel" wide disabled={!premiumOn} busy={actions.isBusy("bufbuy")} onClick={() => actions.run("bufbuy", () => api.academy.createMassScholarships({ wid, vid }, "max_to_buffer", buffer), { success: "Scholarships bought, buffers kept." })}>Buy with buffer</Button>
              </div>
            </Card>
          </>
        )}
      </div>

      <Confirm open={cancelScholar !== null} onClose={() => setCancelScholar(null)} title="Cancel this scholar?" text="The scholar's education is cancelled and the resources come back." action="Cancel scholar" busy={actions.isBusy("cancel_scholar")}
        onConfirm={async () => { if (cancelScholar === null) return; const r = await actions.run("cancel_scholar", () => api.troops.cancelRecruit({ wid, vid }, cancelScholar, "b_a")); if (r) setCancelScholar(null); }} />
    </CityPage>
  );
}

